import { usePutApiProjectsByProjectIdFoldersAndFolderIdDescriptionMutation } from "api/generatedApi";
import type { GetProjectResponse, ProjectFolderResponse, DocumentReferenceResponse } from "api/generatedApi";

import { useDialog } from "shared/dialog/use-dialog";
import { ResultDialogType, useMutation } from "utils/hooks/use-mutation";
import { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DocumentationModal, CreateProjectFormData } from "../documentation-modal";

interface Props {
  project: GetProjectResponse;
  folder: ProjectFolderResponse;
  documents: DocumentReferenceResponse[];
  handleFileChange: (file: File) => Promise<void>;
  fileUploading: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  canDeleteDocument: () => boolean; // function to check if the document can be deleted
  clickDocumentDelete: (documents: DocumentReferenceResponse) => void;
}

export function useDocumentationModal(props: Props) {
  const { project, folder, clickDocumentDelete } = props;
  const { t } = useTranslation();
  const [openDocumentationModal, closeDocumentationModal] = useDialog(DocumentationModal);
  const [editNote] = usePutApiProjectsByProjectIdFoldersAndFolderIdDescriptionMutation();

  const wrapMutation = useMutation({
    onSuccess: closeDocumentationModal,
    successProps: {
      description: t("folder.editDescription.success.description"),
    },
    resultDialogType: ResultDialogType.Toast,
  });

  const handleSubmit: SubmitHandler<CreateProjectFormData> = (data) => {
    wrapMutation(
      editNote({
        projectId: project.id ? project.id : "",
        folderId: folder.projectFolderId ? folder.projectFolderId : "",
        updateProjectFolderDescriptionRequest: {
          projectFolderDescription: data.folderDescription,
        },
      })
    );
  };

  const handleClick = () => {
    openDocumentationModal({
      project: project,
      folder: folder,
      onSubmit: handleSubmit,
      documents: props.documents,
      handleFileChange: props.handleFileChange,
      fileOnChange: props.onChange,
      isFileUploading: props.fileUploading,
      canDeleteDocument: props.canDeleteDocument,
      clickDocumentDelete: clickDocumentDelete,
      onClose: () => {
        closeDocumentationModal();
      },
    });
  };

  return handleClick;
}
